import { useEffect, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { Skeleton, StatusChip } from '@scripsteam/scrips-signal-ds';
// GAP: no DS icon for "insurance / coverage verified" (ShieldCheck); lucide in both modes.
import { ShieldCheck } from 'lucide-react';
// PROD-2743 design sheet §3 (icons): DS names ON only (?ef=frameV2); OFF keeps today's lucide icon, unchanged.
import { useCalculatePrice } from '@/services/scheduling/use-calculate-price';
import { addedServicesToIndividualServices } from '@/scheduling/lib/individual-services';
import { SPONSOR_LABELS } from '@/scheduling/lib/sponsor-labels';
import type {
  HealthInsurance,
  CorporateAgreement,
} from '@/services/scheduling/use-prebook-selected-patient';
import type { AppointmentFormValues } from '@/types/appointment-form';

/**
 * Payment / Sponsor section — Flutter `payment_widget.dart` +
 * `insurance_child_widget.dart` + `corporate_child_widget.dart`.
 *
 * Layout (Flutter parity):
 *   - SPONSOR header
 *   - Three icon tabs: Self-pay / Insurance / Corporate
 *   - For Insurance / Corporate: an "INSURANCE" / "CORPORATE" sub-header
 *     with an Edit link, a horizontal scrollable carousel of the patient's
 *     pre-loaded sponsors, and a detail card showing description +
 *     Exp Date for the picked sponsor.
 *   - Always: a breakdown card with five rows — Gross Total / Discount
 *     Total / Net Total / Patient Payable / Sponsor Amount Total.
 *
 * Selection updates `sponsorId` + `policyNo` + full `sponsor` payer
 * object → `useCalculatePrice` (queryKey'd) re-fires once → breakdown
 * updates from `invoiceTotals`.
 */

interface PaymentSectionProps {
  organizationId: string;
  practiceId: string;
  currency?: string;
  /** Open the Insurance / Corporate edit screens (Flutter "Edit" link). */
  onEditInsurance?: () => void;
  onEditCorporate?: () => void;
  /** Open the edit screens from the empty state ("Add" button). */
  onAddInsurance?: () => void;
  onAddCorporate?: () => void;
  /** Edit mode: the edited appointment's invoice status, drawn under the totals as the design's info chip. */
  invoiceStatus?: string | null;
}

function payerName(payer: { payerName?: string; name?: string } | undefined): string {
  return payer?.payerName ?? payer?.name ?? '';
}

function isExpiredIso(iso: string | null | undefined): boolean {
  if (!iso) return false;
  const t = Date.parse(iso);
  return Number.isFinite(t) && t < Date.now();
}

function formatExpDate(iso: string | null | undefined): string {
  if (!iso) return 'N/A';
  const t = Date.parse(iso);
  if (!Number.isFinite(t)) return 'N/A';
  // Flutter `scDateFormat` → e.g. "Dec 31, 2026"
  return new Date(t).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

interface SponsorChipProps {
  imageUrl?: string;
  name: string;
  isSelected: boolean;
  isVerified?: boolean;
  isExpired?: boolean;
  onClick: () => void;
}

/** A payer chip as the design's (SchedBookingForm `.payer`): the payer's name on a hairline pill, the chosen one blue.
 *  PA extras kept in the same chip: the verified shield before the name and the expired name in red
 *  (Flutter `insurance_child_widget.dart:139-247`). `imageUrl` is no longer drawn (the design's chip is words only). */
function SponsorChip({
  name,
  isSelected,
  isVerified,
  isExpired: expired,
  onClick,
}: SponsorChipProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={name}
      aria-pressed={isSelected}
      data-selected={isSelected || undefined}
      data-expired={expired || undefined}
      className="booking-payer"
    >
      {isVerified ? <ShieldCheck className="booking-payer-verified" aria-hidden /> : null}
      <span className="truncate">{name || '—'}</span>
    </button>
  );
}

interface SelectedSponsorDetailProps {
  /** "TPA • PayerName • Policy" composite line. */
  description: string;
  expirationDate?: string | null;
  /** Insurance only — indicator tags above the description.
   *  Flutter `insurance_child_widget.dart:354–506` `selectedInsuranceView`. */
  indicators?: string[];
  onIndicatorClick?: () => void;
  /** Sponsor-aware expired text — "Insurance Expired" / "Corporate Expired"
   *  (Flutter insurance_child_widget.dart:478 / corporate_child_widget.dart:463).
   *  This component is shared by both sponsor types, so the caller supplies it.
   *  Falls back to the neutral label when null/empty/omitted. */
  expiredLabel?: string | null;
}

function SelectedSponsorDetail({
  description,
  expirationDate,
  indicators,
  onIndicatorClick,
  expiredLabel,
}: SelectedSponsorDetailProps) {
  const expired = isExpiredIso(expirationDate);
  return (
    <div className="booking-payer-detail">
      <p className="booking-card-title truncate">{description}</p>
      <p className="booking-card-meta">
        <span>Exp Date: </span>
        {expirationDate ? (
          expired ? (
            <span className="booking-text-error">
              {expiredLabel || SPONSOR_LABELS.expiredFallback}
            </span>
          ) : (
            <span>{formatExpDate(expirationDate)}</span>
          )
        ) : (
          <span>N/A</span>
        )}
      </p>
      {indicators && indicators.length > 0 ? (
        <div className="booking-flag-row">
          {indicators.map((indicator) => (
            <button
              key={indicator}
              type="button"
              onClick={onIndicatorClick}
              className="booking-flag-button"
            >
              <StatusChip semantic="warning">{indicator}</StatusChip>
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}

/** Indicators row for selected insurance — Flutter `insurance_child_widget.dart`. */
function insuranceIndicators(ins: HealthInsurance): string[] {
  const out: string[] = [];
  if (!ins.frontPhotoUrl || !ins.backPhotoUrl) out.push('Add Card Photos');
  if (isExpiredIso(ins.expirationDate)) out.push('Expired');
  if (!ins.isVerified) out.push('Verify Insurance');
  return out;
}

/**
 * Breakdown rows differ per tab — Flutter
 *   self_pay_child_widget.dart:52-129  → Gross / Discount / Net
 *   corporate_child_widget.dart:251-318 → Gross / Discount / Net / Patient Payable
 *   insurance_child_widget.dart:252-333 → Gross / Discount / Net / Patient Payable / Sponsor Amount Total
 */
type BreakdownVariant = 'self-pay' | 'insurance' | 'corporate';

function BreakdownCard({
  variant,
  gross,
  discount,
  net,
  patientPayable,
  sponsorOutstanding,
  currency,
}: {
  variant: BreakdownVariant;
  gross: number;
  discount: number;
  net: number;
  patientPayable: number;
  sponsorOutstanding: number;
  currency: string;
}) {
  const fmt = (v: number) => `${currency} ${v.toFixed(2)}`;
  // The design's totals (SchedBookingForm `.totals` / `.totalRow`): body rows in the secondary ink, Patient Payable strong.
  const Row = ({ label, value, strong }: { label: string; value: string; strong?: boolean }) => (
    <div className="booking-total-row" data-strong={strong || undefined}>
      <span>{label}</span>
      <span>{value}</span>
    </div>
  );
  return (
    <div className="booking-totals">
      <Row label="Gross Total" value={fmt(gross)} />
      <Row label="Discount Total" value={fmt(discount)} />
      <Row label="Net Total" value={fmt(net)} />
      {variant !== 'self-pay' ? (
        <Row label="Patient Payable" value={fmt(patientPayable)} strong />
      ) : null}
      {variant === 'insurance' ? (
        <Row label="Sponsor Amount Total" value={fmt(sponsorOutstanding)} />
      ) : null}
    </div>
  );
}

export function PaymentSection({
  practiceId: _practiceId,
  organizationId: _organizationId,
  currency = 'AED',
  onEditInsurance,
  onEditCorporate,
  onAddInsurance,
  onAddCorporate,
  invoiceStatus,
}: PaymentSectionProps) {
  const { watch, setValue } = useFormContext<AppointmentFormValues>();
  const sponsorType = watch('sponsorType');
  const providerId = watch('providerId');
  const patientId = watch('patientId');
  const location = watch('location');
  const billingProfileId = watch('billingProfileId');
  const sponsorId = watch('sponsorId');
  const policyNo = watch('policyNo');
  const invoiceTotals = watch('invoiceTotals');
  const healthInsurances = (watch('healthInsurances') ?? []) as HealthInsurance[];
  const corporateAgreements = (watch('corporateAgreements') ?? []) as CorporateAgreement[];
  const addedServices = watch('addedServices');

  const paymentType: 'Self-pay' | 'Insurance' | 'Corporate' =
    (sponsorType ?? 'Self-Pay') === 'Self-Pay'
      ? 'Self-pay'
      : (sponsorType as 'Insurance' | 'Corporate');
  // Memoized so the price re-calc only re-runs when an actual input changes,
  // not on every render (PR #474 review). React Query already dedups by hashed
  // queryKey, but memoizing also avoids rebuilding the individualServices array
  // each render. `addedServices` is a stable RHF watch reference between edits.
  const calculateParams = useMemo(
    () =>
      providerId && patientId && location?.id
        ? {
            providerId,
            patientId,
            locationId: location.id,
            billingProfileId,
            sponsorId,
            paymentType,
            // PROD-1025 (F-SCH-182/184): forward the user's selected services so
            // the pre-book price reflects them (previously hardcoded []). qty
            // defaults to 1 per Flutter IndividualService.toJson.
            individualServices: addedServicesToIndividualServices(addedServices),
          }
        : null,
    [
      providerId,
      patientId,
      location?.id,
      billingProfileId,
      sponsorId,
      paymentType,
      addedServices,
    ],
  );
  const calculatePrice = useCalculatePrice(calculateParams);

  useEffect(() => {
    if (calculatePrice.data) setValue('invoiceTotals', calculatePrice.data);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [calculatePrice.data]);

  // Auto-select first sponsor on tab change — Flutter
  // payment_widget.dart:151-195.
  useEffect(() => {
    if (sponsorType === 'Insurance') {
      const first = healthInsurances[0];
      if (first?.insuranceProvider?.id) {
        setValue('sponsorId', first.insuranceProvider.id);
        setValue('policyNo', first.policyNumber ?? null);
        setValue('sponsor', {
          id: first.insuranceProvider.id,
          payerName: first.insuranceProvider.payerName,
          name: first.insuranceProvider.name,
          imageUrl: first.insuranceProvider.imageUrl,
          payerCompanyId: first.insuranceProvider.payerCompanyId,
          relatedTpa: first.insuranceProvider.relatedTpa,
        });
      } else {
        setValue('sponsorId', null);
        setValue('policyNo', null);
        setValue('sponsor', null);
      }
    } else if (sponsorType === 'Corporate') {
      const first = corporateAgreements[0];
      if (first?.corporateProvider?.id) {
        setValue('sponsorId', first.corporateProvider.id);
        setValue('policyNo', first.policyNumber ?? null);
        setValue('sponsor', {
          id: first.corporateProvider.id,
          payerName: first.corporateProvider.payerName,
          name: first.corporateProvider.name,
          imageUrl: first.corporateProvider.imageUrl,
          payerCompanyId: first.corporateProvider.payerCompanyId,
          relatedTpa: first.corporateProvider.relatedTpa,
        });
      } else {
        setValue('sponsorId', null);
        setValue('policyNo', null);
        setValue('sponsor', null);
      }
    } else {
      setValue('sponsorId', null);
      setValue('policyNo', null);
      setValue('sponsor', null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sponsorType, healthInsurances, corporateAgreements]);

  const selectInsurance = (item: HealthInsurance) => {
    if (!item.insuranceProvider?.id) return;
    setValue('sponsorId', item.insuranceProvider.id);
    setValue('policyNo', item.policyNumber ?? null);
    setValue('sponsor', {
      id: item.insuranceProvider.id,
      payerName: item.insuranceProvider.payerName,
      name: item.insuranceProvider.name,
      imageUrl: item.insuranceProvider.imageUrl,
      payerCompanyId: item.insuranceProvider.payerCompanyId,
      relatedTpa: item.insuranceProvider.relatedTpa,
    });
  };

  const selectCorporate = (item: CorporateAgreement) => {
    if (!item.corporateProvider?.id) return;
    setValue('sponsorId', item.corporateProvider.id);
    setValue('policyNo', item.policyNumber ?? null);
    setValue('sponsor', {
      id: item.corporateProvider.id,
      payerName: item.corporateProvider.payerName,
      name: item.corporateProvider.name,
      imageUrl: item.corporateProvider.imageUrl,
      payerCompanyId: item.corporateProvider.payerCompanyId,
      relatedTpa: item.corporateProvider.relatedTpa,
    });
  };

  const setTab = (next: 'Self-Pay' | 'Insurance' | 'Corporate') =>
    setValue('sponsorType', next, { shouldValidate: true });

  // Description line (Flutter `insurance_child_widget.dart:377-378`)
  // = "{TPA companyName} • {payerName} • {policyNumber}". TPA can be
  // missing — the leading bullet drops with it.
  const selectedInsurance = healthInsurances.find(
    (ins) =>
      !!ins.insuranceProvider?.id &&
      ins.insuranceProvider.id === sponsorId &&
      (ins.policyNumber ?? '') === (policyNo ?? ''),
  );
  const selectedCorporate = corporateAgreements.find(
    (corp) =>
      !!corp.corporateProvider?.id &&
      corp.corporateProvider.id === sponsorId &&
      (corp.policyNumber ?? '') === (policyNo ?? ''),
  );

  const insuranceDescription = (() => {
    if (!selectedInsurance) return '';
    const provider = selectedInsurance.insuranceProvider;
    const tpa = (provider as unknown as { relatedTpa?: { companyName?: string }[] })
      ?.relatedTpa;
    const tpaName = tpa && tpa.length > 0 ? tpa[0]?.companyName : null;
    const parts = [
      tpaName,
      payerName(provider),
      selectedInsurance.policyNumber,
    ].filter(Boolean);
    return parts.join(' • ');
  })();

  const corporateDescription = (() => {
    if (!selectedCorporate) return '';
    const provider = selectedCorporate.corporateProvider;
    const tpa = (provider as unknown as { relatedTpa?: { companyName?: string }[] })
      ?.relatedTpa;
    const tpaName = tpa && tpa.length > 0 ? tpa[0]?.companyName : null;
    const parts = [
      tpaName,
      payerName(provider),
      selectedCorporate.policyNumber,
    ].filter(Boolean);
    return parts.join(' • ');
  })();

  // The design labels the totals "Total" under every sponsor (Flutter showed it on Self-pay only).
  const renderBreakdown = (variant: BreakdownVariant) => (
    <>
      <p className="booking-sub-label">Total</p>
      {renderTotals(variant)}
    </>
  );
  const renderTotals = (variant: BreakdownVariant) =>
    calculatePrice.isPending && !invoiceTotals ? (
      <Skeleton className="booking-totals-skeleton" />
    ) : (
      <BreakdownCard
        variant={variant}
        gross={invoiceTotals?.grossTotal ?? 0}
        discount={invoiceTotals?.discountTotal ?? 0}
        net={invoiceTotals?.netTotal ?? 0}
        patientPayable={invoiceTotals?.patientPayable ?? 0}
        sponsorOutstanding={invoiceTotals?.sponsorOutstanding ?? 0}
        currency={currency}
      />
    );

  return (
    <div className="booking-section" data-slot="booking-section">
      <label className="booking-section-label">
        Payment
      </label>

      {/* Three sponsor tabs as the design's segmented control (SchedBookingForm `.segmented` / `.seg`): a grey track,
          the chosen one a white raised pill, words only. `sponsorType` mirrors the form value. */}
      <div role="radiogroup" aria-label="Sponsor type" className="booking-segmented">
        {(
          [
            { value: 'Self-Pay', label: 'Self-pay' },
            { value: 'Insurance', label: 'Insurance' },
            { value: 'Corporate', label: 'Corporate' },
          ] as const
        ).map(({ value, label }) => {
          const active = (sponsorType ?? 'Self-Pay') === value;
          return (
            <button
              key={value}
              type="button"
              role="radio"
              aria-checked={active}
              data-on={active || undefined}
              onClick={() => setTab(value)}
              className="booking-seg"
            >
              {label}
            </button>
        );
        })}
      </div>

      {sponsorType === 'Self-Pay' && (
        <>
          {renderBreakdown('self-pay')}
        </>
      )}

      {sponsorType === 'Insurance' && (
        <>
          <div className="booking-sub-head">
            <span className="booking-sub-label">
              Insurance
            </span>
            {onEditInsurance ? (
              <button
                type="button"
                onClick={onEditInsurance}
                className="booking-link"
              >
                Edit
              </button>
            ) : null}
          </div>
          {healthInsurances.length === 0 ? (
            <div className="booking-empty-row">
              <p className="booking-empty">
                No Insurance information has been added.
              </p>
              {onAddInsurance ? (
                <button
                  type="button"
                  onClick={onAddInsurance}
                  className="booking-link"
                >
                  Add
                </button>
              ) : null}
            </div>
          ) : (
            <div className="booking-payers scrollbar-hide">
              {healthInsurances.map((ins, i) => {
                const provider = ins.insuranceProvider;
                const name = payerName(provider);
                const selected =
                  !!provider?.id &&
                  provider.id === sponsorId &&
                  (ins.policyNumber ?? '') === (policyNo ?? '');
                return (
                  <SponsorChip
                    key={ins.healthInsuranceId ?? `ins-${i}`}
                    imageUrl={provider?.imageUrl}
                    name={name}
                    isSelected={selected}
                    isVerified={ins.isVerified}
                    isExpired={isExpiredIso(ins.expirationDate)}
                    onClick={() => selectInsurance(ins)}
                  />
                );
              })}
            </div>
          )}
          {selectedInsurance ? (
            <SelectedSponsorDetail
              description={insuranceDescription}
              expirationDate={selectedInsurance.expirationDate}
              indicators={insuranceIndicators(selectedInsurance)}
              onIndicatorClick={onEditInsurance}
              expiredLabel={SPONSOR_LABELS.insuranceExpired}
            />
          ) : null}
          {renderBreakdown('insurance')}
        </>
      )}

      {sponsorType === 'Corporate' && (
        <>
          <div className="booking-sub-head">
            <span className="booking-sub-label">
              Corporate
            </span>
            {onEditCorporate ? (
              <button
                type="button"
                onClick={onEditCorporate}
                className="booking-link"
              >
                Edit
              </button>
            ) : null}
          </div>
          {corporateAgreements.length === 0 ? (
            <div className="booking-empty-row">
              <p className="booking-empty">
                No Corporate agreement information has been added.
              </p>
              {onAddCorporate ? (
                <button
                  type="button"
                  onClick={onAddCorporate}
                  className="booking-link"
                >
                  Add
                </button>
              ) : null}
            </div>
          ) : (
            <div className="booking-payers scrollbar-hide">
              {corporateAgreements.map((corp, i) => {
                const provider = corp.corporateProvider;
                const name = payerName(provider);
                const selected =
                  !!provider?.id &&
                  provider.id === sponsorId &&
                  (corp.policyNumber ?? '') === (policyNo ?? '');
                return (
                  <SponsorChip
                    key={corp.corporateAgreementId ?? `corp-${i}`}
                    imageUrl={provider?.imageUrl}
                    name={name}
                    isSelected={selected}
                    isVerified={corp.isVerified}
                    isExpired={isExpiredIso(corp.expirationDate)}
                    onClick={() => selectCorporate(corp)}
                  />
                );
              })}
            </div>
          )}
          {selectedCorporate ? (
            <SelectedSponsorDetail
              description={corporateDescription}
              expirationDate={selectedCorporate.expirationDate}
              expiredLabel={SPONSOR_LABELS.corporateExpired}
            />
          ) : null}
          {renderBreakdown('corporate')}
        </>
      )}
      {invoiceStatus ? (
        <>
          <p className="booking-sub-label">Invoice status</p>
          <StatusChip semantic="info">{invoiceStatus}</StatusChip>
        </>
      ) : null}
    </div>
  );
}
